<template>
    <div class="discussion">
        <div v-if="isPurchaseDiscussion" class="discussion__warning">
            {{ $t('P2P_CHAT_COMMUNICATION_NOTICE') }}
        </div>

        <div v-if="!messagesLoaded" class="discussion__loading">
            <Loader />
        </div>
        <div v-else-if="messages.length === 0" class="discussion__empty">
            {{ $t('NO_MESSAGES_YET') }}
        </div>
        <div v-else>
            <div class="discussion__messages">
                <transition-group name="message" tag="div">
                    <div v-for="message in topLevelMessages" :key="message.id" class="discussion__message-wrapper"
                        :class="{ 'discussion__message-wrapper--new': message.isNew }">
                        <DiscussionMessage :message="message" :replies="getRepliesForMessage(message.id)" :topic="topic"
                            :nesting-level="0" :canCommentAsUser="canCommentAsUser"
                            :canCommentAsStaff="canCommentAsStaff" :disallowReplies="disallowNestedComments"
                            @reply="handleReply" @report="handleReportMessage"
                            @load-more-replies="handleLoadMoreReplies" />
                    </div>
                </transition-group>
            </div>
        </div>

        <div v-if="canComment" class="discussion__comment-buttons">
            <template v-if="needsNameInput">
                <div class="discussion__name-form">
                    <h3>{{ $t('ENTER_NAME_TO_COMMENT') }}</h3>
                    <div class="discussion__name-input">
                        <VInput v-model="customerName" :placeholder="$t('NAME_PLACEHOLDER')"
                            class="muted-placeholder" />
                        <Button @click="saveName" :disabled="!customerName">{{ $t('SAVE') }}</Button>
                    </div>
                </div>
            </template>

            <template v-else>
                <template v-if="canCommentAsUser && canCommentAsStaff">
                    <Button @click="openUserComposer" class="discussion__comment-button" :disabled="isPostingLocked">
                        {{ $t('POST_MESSAGE') }}
                    </Button>
                    <Button @click="openStaffComposer" class="discussion__comment-button" type="primary"
                        :disabled="isPostingLocked">
                        {{ $t('COMMENT_AS_DEVELOPER_STAFF') }}
                    </Button>
                </template>

                <template v-else-if="canCommentAsUser">
                    <Button @click="openUserComposer" class="discussion__comment-button" :disabled="isPostingLocked">
                        {{ $t('POST_MESSAGE') }}
                    </Button>
                </template>

                <template v-else-if="canCommentAsStaff">
                    <Button @click="openStaffComposer" class="discussion__comment-button" type="primary"
                        :disabled="isPostingLocked">
                        {{ $t('COMMENT_AS_DEVELOPER_STAFF') }}
                    </Button>
                </template>
            </template>
        </div>

        <div v-if="!canComment && disabledReason" class="discussion__disabled">
            {{ disabledReason }}
        </div>

        <ReportModal ref="reportModal" @submit-report="submitReport" />
        <MessageComposerModal ref="messageComposer" :topic="topic" :isPrivate="isPrivate" :forceType="forceType"
            :isPostingLocked="isPostingLocked" @submit-message="handleMessageSubmit" />
        <VModal name="reply-type-selector" :width="320" height="auto" :close-button="true" scrollable>
            <div class="reply-type-selector">
                <h3 class="reply-type-selector__title">{{ $t('REPLY_AS') }}</h3>
                <p class="reply-type-selector__text">{{ $t('HOW_WOULD_YOU_LIKE_TO_REPLY') }}</p>
                <div class="reply-type-selector__buttons">
                    <Button @click="handleCancelReplyTypeSelection" class="reply-type-selector__button"
                        type="secondary">
                        {{ $t('CANCEL') }}
                    </Button>
                    <Button @click="handleReplyAsUser" class="reply-type-selector__button">
                        {{ $t('REPLY_AS_USER') }}
                    </Button>
                    <Button @click="handleReplyAsStaff" class="reply-type-selector__button" type="primary">
                        {{ $t('REPLY_AS_DEVELOPER_STAFF') }}
                    </Button>
                </div>
            </div>
        </VModal>
    </div>
</template>

<script>
import Vue from 'vue';
import VModal from '@/components/VModal.vue';
import DiscussionMessage from './DiscussionMessage.vue';
import Button from './Button.vue';
import VTextarea from './VTextarea.vue';
import VInput from './VInput.vue';
import Checkbox from './Checkbox.vue';
import Loader from './Loader.vue';
import ReportModal from './modals/ReportModal.vue';
import MessageComposerModal from './modals/MessageComposerModal.vue';

export default {
  name: 'Discussion',
  components: {
    DiscussionMessage,
    Button,
    VTextarea,
    VInput,
    Checkbox,
    Loader,
    ReportModal,
    MessageComposerModal,
    VModal,
  },
  props: {
    topic: {
      type: String,
      required: true,
    },
    isPrivate: {
      type: Boolean,
      default: false,
    },
    forceType: {
      type: String,
      default: null,
      validator: value => value === null || value === 'public' || value === 'private',
    },
    canCommentAsUser: {
      type: Boolean,
      default: false,
    },
    canCommentAsStaff: {
      type: Boolean,
      default: false,
    },
    disabledReason: {
      type: String,
      default: '',
    },
    isPurchaseDiscussion: {
      type: Boolean,
      default: false,
    },
    disallowNestedComments: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      messages: [],
      messagesLoaded: false,
      newMessage: '',
      replyingTo: null,
      isPrivateReply: false,
      customerName: '',
      fileUploading: false,
      total: 0,
      isPostingLocked: false,
      postCooldownSeconds: 10,
      postCooldownTimer: null,
      currentReplyMessage: null,
    };
  },
  computed: {
    isCustomerMode() {
      return !!this.$store.state.customerProfile?.email;
    },
    isStaffMode() {
      console.log('isStaffMode check', this.$store.state.publisherProfile?.email);
      return !!this.$store.state.publisherProfile?.email;
    },
    canComment() {
      return (this.canCommentAsUser && this.isCustomerMode)
                || (this.canCommentAsStaff && this.isStaffMode);
    },
    needsNameInput() {
      return this.isCustomerMode && !this.$store.state.customerProfile?.name;
    },
    topLevelMessages() {
      return this.messages.filter(message => !message.reply_to_id);
    },
  },
  mounted() {
    if (this.topic) {
      this.loadProfiles();
    }
  },
  methods: {
    async loadProfiles() {
      try {
        if (!this.$store.state.customerProfile?.id) {
          const customerProfile = await Vue.prototype.$api.getCustomerProfile({
            st: this.$store.state.st,
            lt: this.$store.state.lt,
          });
          if (customerProfile.success) {
            this.$store.commit('setCustomerProfile', customerProfile.data);
          }
        }

        if (!this.$store.state.publisherProfile?.id) {
          const publisherProfile = await Vue.prototype.$api.getPublisherProfile({
            st: this.$store.state.st,
            lt: this.$store.state.lt,
          });
          console.log('publisherProfile', publisherProfile);
          if (publisherProfile.success) {
            this.$store.commit('setPublisherProfile', publisherProfile.data);
          }
        }

        this.fetchMessages();
      } catch (error) {
        console.error('Failed to load profiles:', error);
        this.fetchMessages();
      }
    },

    async fetchMessages() {
      try {
        const response = await Vue.prototype.$api.post('get_messages', {
          topic: this.topic,
          start: 0,
          length: 100,
          st: this.$store.state.st,
          lt: this.$store.state.lt,
        }, false);

        if (response.success) {
          this.messages = response.data;
          this.total = response.total;
        }
      } catch (error) {
        console.error('Failed to fetch messages:', error);
      } finally {
        this.messagesLoaded = true;
      }
    },

    getAllMessages() {
      // Create a map of all messages by their ID for easy lookup
      const messagesMap = {};
      this.messages.forEach((message) => {
        messagesMap[message.id] = {
          ...message,
          children: [],
        };
      });

      // Build the hierarchical structure
      const rootMessages = [];
      this.messages.forEach((message) => {
        if (message.reply_to_id && messagesMap[message.reply_to_id]) {
          // This is a reply to another message
          messagesMap[message.reply_to_id].children.push(messagesMap[message.id]);
        } else {
          // This is a root-level message
          rootMessages.push(messagesMap[message.id]);
        }
      });

      return {
        messagesMap,
        rootMessages,
      };
    },

    getRepliesForMessage(messageId) {
      // Get immediate replies to this message
      return this.messages.filter(message => message.reply_to_id === messageId);
    },

    handleReply(message) {
      if (this.canCommentAsUser && this.canCommentAsStaff) {
        // Store the message we're replying to
        this.currentReplyMessage = message;

        // Show the custom modal for reply type selection
        this.$modal.show('reply-type-selector');
      } else if (this.canCommentAsStaff) {
        this.openStaffComposer(message);
      } else if (this.canCommentAsUser) {
        this.openUserComposer(message);
      }
    },

    cancelReply() {
      this.replyingTo = null;
    },

    async saveName() {
      if (!this.customerName.trim()) return;

      try {
        const response = await Vue.prototype.$api.post('edit_customer_profile', {
          name: this.customerName,
          st: this.$store.state.st,
          lt: this.$store.state.lt,
        });

        if (response) {
          this.$store.commit('setCustomerProfile', response);
        }
      } catch (error) {
        console.error('Failed to save name:', error);
      }
    },

    requestSupport() {
      // Add a tag to request staff support
      this.newMessage = `${this.newMessage}\n\n@staff ${this.$t('PLEASE_HELP_WITH_ISSUE')}`;
    },

    openUserComposer(replyTo = null) {
      console.log('Opening composer as user with reply to:', replyTo);
      if (this.$refs.messageComposer) {
        this.$refs.messageComposer.openAsUser(replyTo);
      } else {
        console.error('messageComposer ref not found');
      }
    },

    openStaffComposer(replyTo = null) {
      console.log('Opening composer as staff with reply to:', replyTo);
      if (this.$refs.messageComposer) {
        this.$refs.messageComposer.openAsStaff(replyTo);
      } else {
        console.error('messageComposer ref not found');
      }
    },

    async handleMessageSubmit(data) {
      if (!data.message || this.isPostingLocked) return;

      try {
        // Set the posting lock
        this.isPostingLocked = true;

        const requestData = {
          topic: this.topic,
          message: data.message,
          as_staff: data.asStaff ? 1 : 0,
          is_private: (this.isPrivate || data.isPrivateReply) ? 1 : 0,
          st: this.$store.state.st || '',
          lt: this.$store.state.lt || '',
        };

        // Add reply info if replying to a message
        if (data.replyingTo && data.replyingTo.id) {
          requestData.reply_to_id = data.replyingTo.id;
        }

        const response = await Vue.prototype.$api.post('post_message', requestData, true);

        if (response) {
          // Add new message to the list
          response.isNew = true; // Mark for animation
          this.messages.push(response);

          // Remove the new flag after animation completes
          setTimeout(() => {
            const index = this.messages.findIndex(m => m.id === response.id);
            if (index !== -1) {
              this.messages[index].isNew = false;
            }
          }, 1000);

          // Start the cooldown timer
          this.startPostCooldown();

          // Show success notification
          Vue.notify({
            type: 'success',
            text: this.$t('COMMENT_POSTED_SUCCESSFULLY'),
          });
        }

        return true;
      } catch (error) {
        console.error('Failed to post message:', error);
        // Reset the posting lock on error
        this.isPostingLocked = false;

        return false;
      }
    },

    startPostCooldown() {
      // Initialize the countdown
      let remainingTime = this.postCooldownSeconds;

      // Update the timer every second
      this.postCooldownTimer = setInterval(() => {
        remainingTime -= 1;

        if (remainingTime <= 0) {
          // Time's up, unlock posting
          this.isPostingLocked = false;
          clearInterval(this.postCooldownTimer);
          this.postCooldownTimer = null;
        }
      }, 1000);
    },

    // Add beforeDestroy lifecycle hook
    beforeDestroy() {
      // Clear any active timers when component is destroyed
      if (this.postCooldownTimer) {
        clearInterval(this.postCooldownTimer);
      }
    },

    async handleLoadMoreReplies(messageId) {
      try {
        // Get the current message replies
        const currentReplies = this.messages.filter(msg => msg.reply_to_id === messageId);

        // Check if we need to load more replies from the server
        if (currentReplies.length > 0) {
          const response = await Vue.prototype.$api.post('get_messages', {
            topic: this.topic,
            ids: [messageId], // Get specific message and its replies
            st: this.$store.state.st,
            lt: this.$store.state.lt,
          }, false);

          if (response.success && response.data && response.data.length > 0) {
            const loadedMessage = response.data[0];

            // If the message has replies_ids, load those replies
            if (loadedMessage.replies_ids && loadedMessage.replies_ids.length > 0) {
              const repliesResponse = await Vue.prototype.$api.post('get_messages', {
                topic: this.topic,
                ids: loadedMessage.replies_ids,
                st: this.$store.state.st,
                lt: this.$store.state.lt,
              }, false);

              if (repliesResponse.success && repliesResponse.data) {
                // Add any new replies that aren't already in our messages list
                const newReplies = repliesResponse.data.filter(reply => !this.messages.some(m => m.id === reply.id));

                if (newReplies.length > 0) {
                  this.messages = [...this.messages, ...newReplies];
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to load more replies:', error);
      }
    },

    handleReportMessage(message) {
      // Store the message data in the modal component
      this.$refs.reportModal.setMessageData(message);
      // Show the report modal
      this.$modal.show('report-message');
    },

    async submitReport(reportData) {
      if (!reportData.reason || this.isPostingLocked) return;

      const reportMessage = `@staff ${this.$t('I_REPORTED_THIS')}: ${reportData.reason}`;

      try {
        // Set the posting lock
        this.isPostingLocked = true;

        const requestData = {
          topic: this.topic,
          message: reportMessage,
          as_staff: 0, // Always post reports as user
          is_private: 0, // Reports should be public
          st: this.$store.state.st || '',
          lt: this.$store.state.lt || '',
        };

        // If we're reporting a specific message, reply to that message
        if (reportData.messageData && reportData.messageData.id) {
          requestData.reply_to_id = reportData.messageData.id;
        }

        const response = await Vue.prototype.$api.post('post_message', requestData, true);

        if (response) {
          // Add new message to the list
          response.isNew = true; // Mark for animation
          this.messages.push(response);

          // Remove the new flag after animation completes
          setTimeout(() => {
            const index = this.messages.findIndex(m => m.id === response.id);
            if (index !== -1) {
              this.messages[index].isNew = false;
            }
          }, 1000);

          // Start the cooldown timer
          this.startPostCooldown();

          // Show success notification
          Vue.notify({
            type: 'success',
            text: this.$t('REPORT_SUBMITTED_SUCCESSFULLY'),
          });
        }

        return true;
      } catch (error) {
        console.error('Failed to submit report:', error);
        // Reset the posting lock on error
        this.isPostingLocked = false;

        Vue.notify({
          type: 'error',
          text: this.$t('FAILED_TO_SUBMIT_REPORT') + (error.message || this.$t('UNKNOWN_ERROR')),
        });

        return false;
      }
    },

    async handleMessageRead(messageId) {
      try {
        const response = await Vue.prototype.$api.post('set_message_read', {
          id: messageId,
          topic: this.topic,
          st: this.$store.state.st,
          lt: this.$store.state.lt,
        });

        if (response && response.success) {
          // Refresh device info
          await this.$store.dispatch('getDevices');
        }

        return response;
      } catch (error) {
        console.error('Failed to mark message as read:', error);
        throw error;
      }
    },

    // Handle reply type selection
    handleCancelReplyTypeSelection() {
      this.currentReplyMessage = null; // Clear the reference
      this.$modal.hide('reply-type-selector');
    },

    handleReplyAsUser() {
      if (this.currentReplyMessage) {
        this.openUserComposer(this.currentReplyMessage);
        this.currentReplyMessage = null; // Clear after use
      }
      this.$modal.hide('reply-type-selector');
    },

    handleReplyAsStaff() {
      if (this.currentReplyMessage) {
        this.openStaffComposer(this.currentReplyMessage);
        this.currentReplyMessage = null; // Clear after use
      }
      this.$modal.hide('reply-type-selector');
    },
  },
};
</script>

<style lang="scss" scoped>
.discussion {
    margin-top: 10px;

    &__warning {
        background-color: var(--lighter-primary);
        padding: 12px;
        border-radius: 4px;
        margin-bottom: 15px;
        font-weight: 500;
        font-size: 14px;
    }

    &__empty {
        padding: 20px 0;
        text-align: center;
        color: var(--text-secondary);
        font-size: 14px;
    }

    &__loading {
        display: flex;
        justify-content: center;
        padding: 20px 0;
    }

    &__messages {
        margin-top: 25px;
        margin-bottom: 20px;
    }

    &__message-wrapper {
        margin-bottom: 8px;
    }

    &__comment-buttons {
        display: flex;
        justify-content: center;
        gap: 10px;
        margin-bottom: 20px;
    }

    &__comment-button {
        min-width: 150px;
    }

    &__name-form {
        margin: 0 auto 15px;
        max-width: 500px;
        text-align: center;

        h3 {
            margin-bottom: 8px;
            font-weight: 500;
            font-size: 15px;
        }
    }

    &__name-input {
        display: flex;
        gap: 8px;
        justify-content: center;
    }

    &__disabled {
        padding: 20px;
        text-align: center;
        color: var(--text-secondary);
        background-color: var(--bg-secondary);
        border-radius: 5px;
    }
}

// Reply type selector modal styles
.reply-type-selector {
    padding: 20px;

    &__title {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 10px;
        color: var(--text-primary);
    }

    &__text {
        margin-bottom: 20px;
        color: var(--text-secondary);
        font-size: 14px;
    }

    &__buttons {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    &__button {
        width: 100%;
    }
}

.message-enter-active,
.message-leave-active {
    transition: all 0.5s;
}

.message-enter,
.message-leave-to {
    opacity: 0;
    transform: translateY(-20px);
}

.discussion__message-wrapper--new {
    animation: highlight-new-message 2s ease-out;
}

@keyframes highlight-new-message {
    0% {
        background-color: rgba(var(--primary-rgb, 0, 123, 255), 0.2);
        transform: translateY(-10px);
    }

    70% {
        background-color: rgba(var(--primary-rgb, 0, 123, 255), 0.05);
    }

    100% {
        background-color: transparent;
        transform: translateY(0);
    }
}

.muted-placeholder {
    ::placeholder {
        color: var(--text-tertiary, #999);
        opacity: 0.6;
    }
}
</style>
